import { useAtomValue } from 'jotai';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';

import { opalAuthAtom } from '@frontend/atoms';
import { OKTA_LOGOUT_URL } from '@frontend/envConfig';
import { useInstanceId } from '@frontend/hooks';
import { sendSentryError } from '@frontend/utils/sendSentryError';

import { GlobalHeaderSkeleton } from './GlobalHeaderSkeleton';
import { optiGlobalHeaderStyles } from './OptiGlobalHeader.css';

type OptiGlobalHeaderProps = {
  visible?: boolean;
  children?: ReactNode;
};

export function OptiGlobalHeader({ visible = true, children }: OptiGlobalHeaderProps) {
  const { authState, oktaAuth } = useOktaAuth();
  const headerRef = useRef<HTMLDivElement>(null);
  const { instanceId } = useInstanceId();
  const opalAuth = useAtomValue(opalAuthAtom);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    // Stencil adds the 'hydrated' class once the custom element has fully rendered.
    if (el.classList.contains('hydrated')) {
      setIsHydrated(true);
      return;
    }

    const observer = new MutationObserver(() => {
      if (el.classList.contains('hydrated')) {
        setIsHydrated(true);
        observer.disconnect();
      }
    });
    observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const onLogout = useCallback(() => {
    (opalAuth?.logout() ?? Promise.resolve())
      .catch(() => {})
      .finally(() =>
        oktaAuth
          .signOut({
            revokeAccessToken: true,
            revokeRefreshToken: true,
            postLogoutRedirectUri: OKTA_LOGOUT_URL,
          })
          .catch(err => sendSentryError(err))
      );
  }, [oktaAuth, opalAuth]);

  useEffect(() => {
    const currentHeaderRef = headerRef.current;
    currentHeaderRef?.addEventListener?.('@opti-common-header:logout', onLogout);

    return () => {
      currentHeaderRef?.removeEventListener?.('@opti-common-header:logout', onLogout);
    };
  }, [headerRef, onLogout]);
  if (!instanceId) {
    return null;
  }
  return (
    <div className={visible ? optiGlobalHeaderStyles.visible : optiGlobalHeaderStyles.hidden}>
      {!isHydrated && <GlobalHeaderSkeleton>{children}</GlobalHeaderSkeleton>}
      <opti-common-header
        ref={headerRef}
        auth-token={authState?.accessToken?.accessToken}
        product-name="Opal"
        instance-id={instanceId}
        version="2"
      >
        {isHydrated && children && <div slot="system-component">{children}</div>}
      </opti-common-header>
    </div>
  );
}
